/**
 * THE ONE "THIS WILL RESET WHAT FOLLOWS" CONFIRMATION FOR INTAKE.
 *
 * Extracted 2026-09-17 from StepIntake's inline "Edit the requirement?"
 * dialog, so the category override's cascade (Ashok ruling, option B) confirms
 * with the SAME dialog rather than a second one. Both uses mark
 * STEP_DEPENDENTS.intake stale and both destroy downstream content the buyer
 * confirmed, so both say the same thing about what survives.
 *
 * WHAT DIFFERS BETWEEN THE TWO USES, AND WHY - only the lead and the labels:
 *   - editing the requirement RE-ANALYZES it ("re-analyze it and regenerate"),
 *   - replacing a retired category does not re-analyze anything; the header is
 *     simply replaced, so the lead says only that downstream is regenerated.
 * The sentence about what is kept and what is reset is shared, not restated.
 *
 * `onCancel` runs for Cancel AND for any other dismissal (Escape, overlay), so
 * a caller waiting on the decision is always released.
 */
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

// Copy fix (2026-07-27), kept verbatim from the original dialog: the old text
// claimed answers/edits "will be replaced" - actually reconcileAnswersForQuestions
// (WizardContext.jsx) keeps an answer whenever its question (matched by
// field_key, falling back to content) survives regeneration, and only resets
// it when the question is removed or changed.
export const DOWNSTREAM_KEPT_AND_RESET =
  "Answers you've already given are kept where questions still apply; anything tied to removed or changed questions will be reset.";

export const INTAKE_EDIT_VARIANT = {
  title: 'Edit the requirement?',
  lead: 'Editing the requirement will re-analyze it and regenerate downstream content.',
  confirmLabel: 'Edit Anyway',
  confirmTestId: 'confirm-edit-intake-btn',
};

export const CATEGORY_REPLACE_VARIANT = {
  title: 'Replace the category?',
  // REWRITTEN 2026-09-18, because what this sentence described changed under
  // it. The previous lead - "marks the later steps to be regenerated as you
  // continue through them" - was written when the cascade was client-side step
  // marks, and its comment said in as many words that "the marks do not
  // survive a reload" and that "nothing blocks a buyer from skipping ahead".
  // BOTH ARE NOW FALSE: a confirmed override regresses the STORED status, so
  // the effect survives a reload and the wizard caps forward navigation at the
  // regressed step. A dialog describing the weaker old behaviour would now
  // understate what the buyer is agreeing to.
  //
  // IT MUST ALSO NOT IMPLY THE RFP IS DELETED, because it is not - the buyer
  // may already have sent it to vendors, and it stays on the record until
  // generate_rfp replaces it. Hence the second sentence, which says so
  // outright rather than leaving it to be inferred from silence.
  //
  // Worded to be true at EVERY status. A document still at intake has no
  // completed steps to revisit and no stored RFP, and this sentence says
  // nothing false about it either.
  //
  // "STAYS ON THE RECORD" WAS READING AS "STAYS VALID" (2026-09-18, item 5),
  // and it does not - the stored RFP is not reissued when the header changes.
  // It says "unchanged" rather than naming what the document DISPLAYS,
  // because the header category reaches the .docx in exactly one place: the
  // LAST-RESORT title fallback in _derive_document_title, used only when the
  // agent's document_title and all three prose candidates fail, and then as
  // the category NAME, never the code. On an ordinary document the header
  // category appears nowhere in the RFP, so "still showing the previous
  // category" was false for most of them. (The Requirement Overview
  // attribute table that once carried it was removed on 2026-07-28; the
  // comment in document_generator.py claiming category "still shows up once,
  // at requirement level" is stale.)
  //
  // THE RISK HALF IS GONE (2026-09-18/J). It was kept a day earlier because
  // completed -> finalize_questions -> refined strands a risk assessment
  // under a status that no longer refuses the override - true at the
  // ENDPOINT, and asserted in backend/tests/test_at_or_before_positive_
  // control.py. Ashok then tested it in the browser: the wizard offers no
  // navigation back from completed, so the route is API-reachable and NOT
  // BUYER-REACHABLE. For a buyer, risk generated means completed means no way
  // back, and this dialog can never be seen with a risk assessment stored.
  // Naming it described a state its reader cannot be in. The endpoint gap is
  // recorded in memory/CONTROL-REGISTER.md under the freeze boundary; it is
  // not fixed here and the test that demonstrates it stays.
  lead: 'Replacing the category means the steps after Intake are worked through again, and each is regenerated as you reach it. An RFP already generated stays on the record, unchanged, until you regenerate it.',
  confirmLabel: 'Replace Anyway',
  confirmTestId: 'confirm-category-cascade-btn',
};

const DownstreamResetConfirmDialog = ({ open, variant, onConfirm, onCancel }) => (
  <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
    <DialogContent className="sm:max-w-md" data-testid={`${variant.confirmTestId}-dialog`}>
      <DialogHeader>
        <DialogTitle>{variant.title}</DialogTitle>
        <DialogDescription>
          {variant.lead} {DOWNSTREAM_KEPT_AND_RESET}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} data-testid={`${variant.confirmTestId}-cancel`}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
          data-testid={variant.confirmTestId}
        >
          {variant.confirmLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export default DownstreamResetConfirmDialog;
